'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { Loader2, Check, AlertCircle } from 'lucide-react'
import { AddressAutocomplete, type SelectedAddress } from '@/components/shared/AddressAutocomplete'
import { formatDropoffLine } from '@/lib/delivery/formatDropoff'
import {
  buildShipQuoteItems,
  parseShipQuoteResponse,
  shipAddressReadyForAutoQuote,
  type CartLineLike,
} from '@/lib/microsite/shippingCheckout'
import { SHIP_ERROR_UX, type ShipErrorKind } from './shipErrorUx'
import type { ShipQuote, ShipToAddress } from '../types'

/**
 * Paso de dirección del checkout en modo "Envío" (Fase 1b T11) — CALCA el
 * patrón de `AddressStep.tsx` (léelo primero): mismo estilo visual, mismos
 * estados (idle/loading/ok/error), misma regla de "solo cotiza al elegir una
 * sugerencia de Google". Es un componente APARTE (no una rama dentro de
 * AddressStep) porque el backend, la forma del request y las columnas de
 * error no tienen nada en común con delivery: acá se verifica con EasyPost
 * (no hay chequeo de zona por lat/lng), hace falta el NOMBRE del
 * destinatario (EasyPost lo exige y este paso ocurre ANTES de CustomerStep),
 * y el precio depende del CARRITO (peso/bultos), no solo de la dirección —
 * por eso recibe `cart`.
 *
 * El precio del envío tampoco se muestra acá, por el mismo motivo que en
 * AddressStep: aparece en la línea "Envío" de los totales, junto al resto de
 * lo que el cliente paga.
 */

type QuoteState = 'idle' | 'loading' | 'ok' | 'error'

type Props = {
  locationId: string
  accentColor: string
  cart: CartLineLike[]
  /** Dirección + destinatario ya confirmados (vive en el padre: sobrevive al cierre del sheet). */
  shipTo: ShipToAddress | null
  /** Cotización vigente. Si llega junto con `shipTo`, el paso arranca en éxito sin re-cotizar. */
  shipQuote: ShipQuote | null
  /** Cotización OK: el padre congela quote + dirección. El quote se pasa VERBATIM. */
  onQuote: (quote: ShipQuote, shipTo: ShipToAddress) => void
  /** La dirección/carrito dejó de ser válida: el padre borra el quote. */
  onClearQuote: () => void
  /** "Mejor recojo yo": el padre vuelve a pickup y limpia dirección + quote. */
  onSwitchToPickup: () => void
  onContinue: () => void
  onBack: () => void
}

export function ShipAddressStep({
  locationId,
  accentColor,
  cart,
  shipTo,
  shipQuote,
  onQuote,
  onClearQuote,
  onSwitchToPickup,
  onContinue,
  onBack,
}: Props) {
  const t = useTranslations('public.onlineOrder.checkout')
  const tCommon = useTranslations('common')

  const [name, setName] = useState(shipTo?.name ?? '')
  const [text, setText] = useState(shipTo ? formatDropoffLine(shipTo) : '')
  const [selected, setSelected] = useState<SelectedAddress | null>(
    shipTo ? { street: shipTo.street, city: shipTo.city, state: shipTo.state, zip: shipTo.zip } : null,
  )
  const [state, setState] = useState<QuoteState>(shipTo && shipQuote ? 'ok' : 'idle')
  const [errorKind, setErrorKind] = useState<ShipErrorKind | null>(null)
  const [minParcels, setMinParcels] = useState<number | undefined>(undefined)
  // Misma razón que AddressStep: descarta respuestas viejas que lleguen tarde
  // (el cliente eligió otra dirección o el nombre cambió mientras una cotización
  // seguía en vuelo).
  const reqRef = useRef(0)

  async function quote(addr: SelectedAddress, recipientName: string) {
    const myReq = ++reqRef.current
    setSelected(addr)
    setState('loading')
    setErrorKind(null)
    setMinParcels(undefined)
    // La dirección/nombre cambió ⇒ el quote anterior ya no aplica. Se invalida
    // ANTES de pedir el nuevo, mismo criterio que AddressStep.
    onClearQuote()

    let res: Response
    try {
      res = await fetch('/api/shipping/quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locationId,
          items: buildShipQuoteItems(cart),
          shipTo: {
            name: recipientName.trim(),
            street: addr.street,
            city: addr.city,
            state: addr.state,
            zip: addr.zip,
          },
        }),
      })
    } catch {
      // El fetch ni salió: se cayó la red del cliente. Transitorio de verdad.
      if (myReq !== reqRef.current) return
      setState('error')
      setErrorKind('network')
      return
    }

    if (myReq !== reqRef.current) return

    const body = await res.json().catch(() => null) as
      | { quote?: ShipQuote; error?: string; minParcels?: number }
      | null
    const result = parseShipQuoteResponse(res.status, body)

    if (!result.ok) {
      setState('error')
      setErrorKind(result.kind)
      setMinParcels(result.minParcels)
      return
    }

    setState('ok')
    onQuote(result.quote, {
      name: recipientName.trim(),
      street: addr.street,
      city: addr.city,
      state: addr.state,
      zip: addr.zip,
    })
  }

  // Ronda de fix 1, IMPORTANT-4: auto-cotiza AL MONTAR si el paso arranca con
  // dirección + nombre completos pero sin quote vigente — el caso real es
  // volver del carrito con el quote ya invalidado (OrderSheet lo borra al
  // detectar que el carrito cambió, pero CONSERVA `shipTo`: no hace falta
  // volver a tipear nada). Sin esto el CTA quedaba deshabilitado sin
  // explicación: cotizar solo se disparaba al elegir una sugerencia NUEVA o
  // al salir del campo de nombre — ninguno de los dos es obvio cuando el
  // formulario YA está lleno. `autoQuotedRef` lo limita a UNA vez por montaje
  // (el componente se desmonta/remonta en cada visita al paso, ver
  // OrderSheet): no es un auto-retry en bucle.
  const autoQuotedRef = useRef(false)
  useEffect(() => {
    if (autoQuotedRef.current) return
    autoQuotedRef.current = true
    if (
      shipAddressReadyForAutoQuote({
        hasSelectedAddress: !!selected,
        recipientName: name,
        quoteState: state,
        hasActiveQuote: !!shipQuote,
      })
    ) {
      void quote(selected!, name)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleText(v: string) {
    setText(v)
    if (state !== 'idle') {
      setState('idle')
      setSelected(null)
      setErrorKind(null)
      reqRef.current += 1
      onClearQuote()
    }
  }

  function handleName(v: string) {
    setName(v)
    // El nombre SÍ viaja al cotizador (EasyPost lo exige para verificar la
    // dirección) — a diferencia del apto de delivery, cambiarlo invalida el
    // quote y hay que volver a elegir/confirmar.
    if (state === 'ok') {
      setState('idle')
      reqRef.current += 1
      onClearQuote()
    }
  }

  const errorCopy: Record<ShipErrorKind, { title: string; body: string }> = {
    state_not_served: { title: t('shipStateNotServedTitle'), body: t('shipStateNotServedBody') },
    below_min_parcels: {
      title: t('shipBelowMinParcelsTitle'),
      body: t('shipBelowMinParcelsBody', { min: minParcels ?? 2 }),
    },
    address_not_verified: { title: t('shipAddressNotVerifiedTitle'), body: t('shipAddressNotVerifiedBody') },
    not_shippable_items: { title: t('shipNotShippableItemsTitle'), body: t('shipNotShippableItemsBody') },
    too_many_parcels: { title: t('shipTooManyParcelsTitle'), body: t('shipTooManyParcelsBody') },
    rate_limited: { title: t('shipRateLimitedTitle'), body: t('shipRateLimitedBody') },
    daily_quota_exhausted: { title: t('shipDailyQuotaTitle'), body: t('shipDailyQuotaBody') },
    quote_in_use: { title: t('shipQuoteInUseTitle'), body: t('shipQuoteInUseBody') },
    not_available: { title: t('shipNotAvailableTitle'), body: t('shipNotAvailableBody') },
    network: { title: t('deliveryNetworkErrorTitle'), body: t('deliveryNetworkError') },
    quote_failed: { title: t('shipQuoteFailedTitle'), body: t('shipQuoteFailedBody') },
  }
  const errorUx = errorKind ? SHIP_ERROR_UX[errorKind] : null
  const errorWarn = errorUx?.tone === 'warn'

  const canContinue = state === 'ok' && !!shipQuote

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium text-gray-600 hover:text-gray-900"
        >
          ← {t('back')}
        </button>
        <h2 className="flex-1 text-center text-xl font-bold text-gray-900 -ml-12">
          {t('addressShipTitle')}
        </h2>
      </div>

      {/* Scroll area */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        <p className="text-sm text-gray-500">{t('addressShipHelp')}</p>

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="ship-recipient-name">
            {t('shipRecipientNameLabel')}
          </label>
          <input
            id="ship-recipient-name"
            type="text"
            value={name}
            onChange={(e) => handleName(e.target.value)}
            onBlur={() => {
              // El nombre llegó DESPUÉS de elegir la dirección (orden típico:
              // pega/elige la dirección, después escribe a quién va). Recién con
              // los dos completos hay algo que cotizar.
              if (selected && name.trim() && state === 'idle') void quote(selected, name)
            }}
            placeholder={t('shipRecipientNamePlaceholder')}
            maxLength={100}
            autoComplete="name"
            className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
          />
        </div>

        <div className="mt-4">
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t('addressStreetLabel')}
          </label>
          <AddressAutocomplete
            value={text}
            onChange={handleText}
            onPlaceSelect={(p) => {
              setText(formatDropoffLine(p))
              if (name.trim()) void quote(p, name)
              else setSelected(p)
            }}
            placeholder={t('addressStreetPlaceholder')}
            className="w-full rounded-xl border border-gray-200 px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-gray-900/10"
          />
          <p className="mt-1 text-xs text-gray-500">{t('addressPickSuggestion')}</p>
        </div>

        {/* Cotizando */}
        {state === 'loading' && (
          <div className="mt-4 flex items-center gap-2 rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3">
            <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
            <span className="text-sm text-gray-600">{t('shipAddressChecking')}</span>
          </div>
        )}

        {/* Verificada — SIN precio (el envío se ve en los totales) */}
        {state === 'ok' && (
          <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <div className="w-7 h-7 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-white" strokeWidth={3} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-green-900">{t('shipAddressVerifiedTitle')}</p>
                {selected && (
                  <p className="text-xs text-green-800/80 mt-1 break-words">
                    {formatDropoffLine(selected)}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cada fallo con su salida REAL (ver SHIP_ERROR_UX). El pickup está en todos. */}
        {state === 'error' && errorKind && errorUx && (
          <div
            className={`mt-4 rounded-2xl border px-4 py-3 ${
              errorWarn ? 'border-amber-200 bg-amber-50' : 'border-gray-200 bg-gray-50'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <AlertCircle
                className={`w-5 h-5 flex-shrink-0 mt-0.5 ${errorWarn ? 'text-amber-600' : 'text-gray-500'}`}
              />
              <div className="min-w-0">
                <p className={`text-sm font-bold ${errorWarn ? 'text-amber-900' : 'text-gray-900'}`}>
                  {errorCopy[errorKind].title}
                </p>
                <p className={`text-xs mt-0.5 ${errorWarn ? 'text-amber-800' : 'text-gray-600'}`}>
                  {errorCopy[errorKind].body}
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {errorUx.canRetry && (
                <button
                  type="button"
                  onClick={() => selected && void quote(selected, name)}
                  disabled={!selected}
                  className="rounded-full px-4 py-2 text-xs font-bold text-white transition hover:opacity-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: accentColor }}
                >
                  {t('retry')}
                </button>
              )}
              {errorUx.canChangeAddress && (
                <button
                  type="button"
                  onClick={() => handleText('')}
                  className="rounded-full px-4 py-2 text-xs font-bold text-white transition hover:opacity-95"
                  style={{ backgroundColor: accentColor }}
                >
                  {t('addressChangeAddress')}
                </button>
              )}
              <button
                type="button"
                onClick={onSwitchToPickup}
                className="rounded-full border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-700 transition hover:bg-gray-50"
              >
                {t('addressSwitchToPickup')}
              </button>
            </div>
          </div>
        )}

        {/* Faltó el nombre O una sugerencia elegida — sin precio inventado, sin
            aviso rojo hasta que intente avanzar (mismo patrón de "touched" que
            el resto del checkout). */}
        {state === 'idle' && selected && !name.trim() && (
          <p className="mt-2 text-xs text-amber-700">{t('shipRecipientNameRequired')}</p>
        )}
      </div>

      {/* Sticky footer */}
      <div
        className="border-t border-gray-100 bg-white px-5 pt-4"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      >
        <button
          type="button"
          onClick={onContinue}
          disabled={!canContinue}
          className="w-full rounded-full py-4 text-white text-base font-bold transition hover:opacity-95 active:scale-[0.99] disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: accentColor }}
        >
          {canContinue ? `${tCommon('next')} →` : t('addressCtaNeedAddress')}
        </button>
        <button
          type="button"
          onClick={onSwitchToPickup}
          className="mt-2 w-full py-2 text-sm font-semibold text-gray-500 hover:text-gray-800 transition"
        >
          {t('addressSwitchToPickup')}
        </button>
      </div>
    </div>
  )
}
